/**
 * Trace / branch list reads for the v-next Postgres observability domain.
 *
 * - `listTraces` / `getRootSpan` filter by `parentSpanId IS NULL`. The partial
 *   indexes declared in ddl.ts make this predicate selective enough to act as
 *   the root-span projection without a separate table.
 * - `listBranches` filters by `spanType IN (BRANCH_SPAN_TYPES)` (or the
 *   user-supplied spanType when it's a branch type). Branches include nested
 *   anchors as well as root spans of the listed types.
 */

import {
  BRANCH_SPAN_TYPES,
  listBranchesArgsSchema,
  listTracesArgsSchema,
  TraceStatus,
  toTraceSpans,
} from '@mastra/core/storage';
import type {
  GetRootSpanArgs,
  GetRootSpanResponse,
  ListBranchesArgs,
  ListBranchesResponse,
  ListTracesArgs,
  ListTracesResponse,
  SpanRecord,
} from '@mastra/core/storage';

import type { DbClient } from '../../../client';
import { qualifiedTable, TABLE_SPAN_EVENTS } from './ddl';
import { rowToSpanRecord } from './helpers';
import {
  assertDeltaPollingEnabled,
  decodeDeltaCursor,
  deltaPollingFeatureEnabled,
  encodeDeltaCursor,
  readSafeXactHorizon,
} from './polling';
import { SPAN_SELECT_COLUMNS } from './sql';

function asIsoTimestamp(value: unknown): string {
  return value instanceof Date ? value.toISOString() : new Date(value as string | number).toISOString();
}

/**
 * Ordering that picks the row representing a trace's current root.
 *
 * A trace can hold more than one root row. A span is written once when it
 * starts and again when it ends, and a durable run that suspends ends its root
 * span and opens a fresh one on resume. In both cases the newest row is the one
 * that describes the trace right now, and `cursorId` — a sequence assigned at
 * insert — is what "newest" means here. It also orders correctly where
 * timestamps cannot: a zero-duration span has `startedAt = endedAt`, and a
 * resumed root starts after the suspended root ended.
 */
const ROOT_COLLAPSE_ORDER = '"cursorId" DESC';

/**
 * Predicate that keeps only the winning row for each span — the ended row when
 * one exists, the newest row otherwise. Mirrors `SPAN_COLLAPSE_ORDER` as an
 * anti-join, for the same reason as {@link latestRootPredicate}.
 */
function latestSpanPredicate(spanTable: string): string {
  return `NOT EXISTS (
    SELECT 1 FROM ${spanTable} newer
    WHERE newer."traceId" = r."traceId"
      AND newer."spanId" = r."spanId"
      AND (newer."isPending" < r."isPending" OR (newer."isPending" = r."isPending" AND newer."cursorId" > r."cursorId"))
  )`;
}

/**
 * Predicate that keeps only a trace's current root row, as an anti-join rather
 * than a `DISTINCT ON`. Written this way so the outer query still scans the
 * partial root indexes and streams rows in `LIMIT`-order; grouping first would
 * force every root in the table to be read before the first page came back.
 */
function latestRootPredicate(spanTable: string): string {
  return `NOT EXISTS (
    SELECT 1 FROM ${spanTable} newer
    WHERE newer."traceId" = r."traceId"
      AND newer."parentSpanId" IS NULL
      AND newer."cursorId" > r."cursorId"
  )`;
}

export async function getRootSpan(
  client: DbClient,
  schema: string,
  args: GetRootSpanArgs,
): Promise<GetRootSpanResponse | null> {
  const table = qualifiedTable(schema, TABLE_SPAN_EVENTS);
  const row = await client.oneOrNone<Record<string, any>>(
    `SELECT ${SPAN_SELECT_COLUMNS}
     FROM ${table}
     WHERE "traceId" = $1 AND "parentSpanId" IS NULL
     ORDER BY ${ROOT_COLLAPSE_ORDER}
     LIMIT 1`,
    [args.traceId],
  );
  if (!row) return null;
  return { span: rowToSpanRecord(row) };
}

/**
 * Build the WHERE conditions and bind params for a listTraces query against
 * `mastra_span_events r`. Always prepends the root-span predicate so the
 * partial root indexes (and the partial cursor index) get picked up by the
 * planner. Starts numbering from `nextParamIdx`.
 */
function buildListTracesFilters(
  filters: ListTracesArgs['filters'],
  spanTable: string,
  nextParamIdx: number,
): { conditions: string[]; params: unknown[]; nextParamIdx: number } {
  const conditions: string[] = [`r."parentSpanId" IS NULL`, latestRootPredicate(spanTable)];
  const params: unknown[] = [];
  let i = nextParamIdx;

  if (!filters) {
    return { conditions, params, nextParamIdx: i };
  }

  if (filters.startedAt?.start) {
    conditions.push(`r."startedAt" ${filters.startedAt.startExclusive ? '>' : '>='} $${i++}`);
    params.push(asIsoTimestamp(filters.startedAt.start));
  }
  if (filters.startedAt?.end) {
    conditions.push(`r."startedAt" ${filters.startedAt.endExclusive ? '<' : '<='} $${i++}`);
    params.push(asIsoTimestamp(filters.startedAt.end));
  }
  // A pending row stores a placeholder `endedAt`, so an endedAt range filter
  // has to exclude it: a span that has not ended has no end time to match.
  if (filters.endedAt?.start) {
    conditions.push(`(NOT r."isPending" AND r."endedAt" ${filters.endedAt.startExclusive ? '>' : '>='} $${i++})`);
    params.push(asIsoTimestamp(filters.endedAt.start));
  }
  if (filters.endedAt?.end) {
    conditions.push(`(NOT r."isPending" AND r."endedAt" ${filters.endedAt.endExclusive ? '<' : '<='} $${i++})`);
    params.push(asIsoTimestamp(filters.endedAt.end));
  }
  if (filters.spanType !== undefined) {
    conditions.push(`r."spanType" = $${i++}`);
    params.push(filters.spanType);
  }
  if (filters.entityType !== undefined) {
    conditions.push(`r."entityType" = $${i++}`);
    params.push(filters.entityType);
  }
  if (filters.entityId !== undefined) {
    conditions.push(`r."entityId" = $${i++}`);
    params.push(filters.entityId);
  }
  if (filters.entityName !== undefined) {
    conditions.push(`r."entityName" = $${i++}`);
    params.push(filters.entityName);
  }
  if (filters.userId !== undefined) {
    conditions.push(`r."userId" = $${i++}`);
    params.push(filters.userId);
  }
  if (filters.organizationId !== undefined) {
    conditions.push(`r."organizationId" = $${i++}`);
    params.push(filters.organizationId);
  }
  if (filters.resourceId !== undefined) {
    conditions.push(`r."resourceId" = $${i++}`);
    params.push(filters.resourceId);
  }
  if (filters.runId !== undefined) {
    conditions.push(`r."runId" = $${i++}`);
    params.push(filters.runId);
  }
  if (filters.sessionId !== undefined) {
    conditions.push(`r."sessionId" = $${i++}`);
    params.push(filters.sessionId);
  }
  if (filters.threadId !== undefined) {
    conditions.push(`r."threadId" = $${i++}`);
    params.push(filters.threadId);
  }
  if (filters.requestId !== undefined) {
    conditions.push(`r."requestId" = $${i++}`);
    params.push(filters.requestId);
  }
  if (filters.environment !== undefined) {
    conditions.push(`r."environment" = $${i++}`);
    params.push(filters.environment);
  }
  if (filters.source !== undefined) {
    conditions.push(`r."executionSource" = $${i++}`);
    params.push(filters.source);
  }
  if (filters.serviceName !== undefined) {
    conditions.push(`r."serviceName" = $${i++}`);
    params.push(filters.serviceName);
  }
  if (filters.metadata != null) {
    conditions.push(`r."metadataSearch" @> $${i++}::jsonb`);
    params.push(JSON.stringify(filters.metadata));
  }
  if (filters.tags != null && filters.tags.length > 0) {
    conditions.push(`r."tags" @> $${i++}::text[]`);
    params.push(filters.tags);
  }
  if (filters.status !== undefined) {
    switch (filters.status) {
      case TraceStatus.ERROR:
        conditions.push(`r."error" IS NOT NULL`);
        break;
      case TraceStatus.RUNNING:
        conditions.push(`r."isPending"`);
        break;
      case TraceStatus.SUCCESS:
        conditions.push(`r."error" IS NULL AND NOT r."isPending"`);
        break;
    }
  }
  if (filters.hasChildError !== undefined) {
    const sub = `EXISTS (
      SELECT 1 FROM ${spanTable} c
      WHERE c."traceId" = r."traceId" AND c."spanId" <> r."spanId" AND c."error" IS NOT NULL
    )`;
    conditions.push(filters.hasChildError ? sub : `NOT ${sub}`);
  }

  return { conditions, params, nextParamIdx: i };
}

/**
 * Project the standard span columns with the `r.` alias prefix.
 *
 * Built by string-munging `SPAN_SELECT_COLUMNS` (a constant in sql.ts) at
 * module load. This works because every entry in that list is a bare
 * column name like `"traceId"` — no expressions, no functions. If anyone
 * ever adds something like `COALESCE(...)` to `SPAN_SELECT_COLUMNS`, the
 * naive split-on-comma breaks. Keep both lists shaped as plain column
 * names; if you need computed columns, switch to a structured
 * array-of-strings representation here.
 */
const SPAN_SELECT_COLUMNS_ALIASED = SPAN_SELECT_COLUMNS.replace(/\n/g, ' ')
  .split(',')
  .map(c => `r.${c.trim()}`)
  .join(', ');

export async function listTraces(client: DbClient, schema: string, args: ListTracesArgs): Promise<ListTracesResponse> {
  const { mode, filters, pagination, orderBy, after, limit } = listTracesArgsSchema.parse(args);
  const span = qualifiedTable(schema, TABLE_SPAN_EVENTS);

  if (mode === 'delta') {
    assertDeltaPollingEnabled();
    return listTracesDelta(client, span, filters, after, limit);
  }

  return listTracesPage(client, span, filters, pagination.page, pagination.perPage, orderBy.field, orderBy.direction);
}

async function listTracesPage(
  client: DbClient,
  span: string,
  filters: ListTracesArgs['filters'],
  page: number,
  perPage: number,
  orderField: 'startedAt' | 'endedAt',
  orderDir: 'ASC' | 'DESC',
): Promise<ListTracesResponse> {
  const { conditions, params, nextParamIdx } = buildListTracesFilters(filters, span, 1);
  let i = nextParamIdx;
  const whereClause = `WHERE ${conditions.join(' AND ')}`;
  const orderClause =
    orderField === 'endedAt'
      ? `ORDER BY r."endedAt" ${orderDir} NULLS ${orderDir === 'DESC' ? 'FIRST' : 'LAST'}, r."cursorId" ${orderDir}`
      : `ORDER BY r."${orderField}" ${orderDir}, r."cursorId" ${orderDir}`;

  const countRow = await client.oneOrNone<{ count: string }>(
    `SELECT COUNT(*)::text AS count FROM ${span} r ${whereClause}`,
    params,
  );
  const count = Number(countRow?.count ?? 0);

  let spans: SpanRecord[] = [];
  if (count > 0) {
    const rows = await client.manyOrNone<Record<string, any>>(
      `SELECT ${SPAN_SELECT_COLUMNS_ALIASED}
       FROM ${span} r
       ${whereClause}
       ${orderClause}
       LIMIT $${i++} OFFSET $${i++}`,
      [...params, perPage, page * perPage],
    );
    spans = rows.map(rowToSpanRecord);
  }

  const deltaCursor = deltaPollingFeatureEnabled()
    ? await readTracesStreamHeadCursor(client, span, filters)
    : undefined;

  return {
    pagination: { total: count, page, perPage, hasMore: (page + 1) * perPage < count },
    spans: toTraceSpans(spans),
    ...(deltaCursor !== undefined ? { deltaCursor } : {}),
  };
}

async function listTracesDelta(
  client: DbClient,
  span: string,
  filters: ListTracesArgs['filters'],
  after: string | undefined,
  limit: number,
): Promise<ListTracesResponse> {
  if (after === undefined) {
    const deltaCursor = await readTracesStreamHeadCursor(client, span, filters);
    return { spans: [], delta: { limit, hasMore: false }, deltaCursor };
  }

  const afterCursor = decodeDeltaCursor(after);
  const safeHorizon = await readSafeXactHorizon(client);
  const { conditions, params, nextParamIdx } = buildListTracesFilters(filters, span, 1);
  let i = nextParamIdx;
  conditions.push(`(r."xactId", r."cursorId") > ($${i++}::xid8, $${i++}::bigint)`);
  params.push(afterCursor.xactId, afterCursor.cursorId);
  conditions.push(`r."xactId" < $${i++}::xid8`);
  params.push(safeHorizon);

  const rows = await client.manyOrNone<Record<string, any>>(
    `SELECT ${SPAN_SELECT_COLUMNS_ALIASED}
     FROM ${span} r
     WHERE ${conditions.join(' AND ')}
     ORDER BY r."xactId" ASC, r."cursorId" ASC
     LIMIT $${i++}`,
    [...params, limit + 1],
  );

  const hasMore = rows.length > limit;
  const visible = rows.slice(0, limit);
  const deltaCursor =
    visible.length > 0
      ? encodeDeltaCursor(visible[visible.length - 1]!.xactId, visible[visible.length - 1]!.cursorId)
      : encodeDeltaCursor(safeHorizon, 0);

  return {
    spans: toTraceSpans(visible.map(rowToSpanRecord)),
    delta: { limit, hasMore },
    deltaCursor,
  };
}

async function readTracesStreamHeadCursor(
  client: DbClient,
  span: string,
  filters: ListTracesArgs['filters'],
): Promise<string> {
  void span;
  void filters;
  return encodeDeltaCursor(await readSafeXactHorizon(client), 0);
}

// ---------------------------------------------------------------------------
// listBranches
// ---------------------------------------------------------------------------

const BRANCH_SPAN_TYPE_SET = new Set<string>(BRANCH_SPAN_TYPES);

/**
 * Build the spanType predicate for a branch query. If the caller supplied a
 * specific branch type, narrow to that one; otherwise match the full
 * BRANCH_SPAN_TYPES set. Pushes the bind params onto `params` and returns the
 * SQL fragment plus the next param index.
 */
function buildBranchSpanTypeClause(
  userSpanType: string | undefined,
  params: unknown[],
  startIdx: number,
): { clause: string; nextIdx: number } | null {
  if (userSpanType !== undefined) {
    if (!BRANCH_SPAN_TYPE_SET.has(userSpanType)) {
      // Caller asked for a non-branch spanType — intersection is empty.
      return null;
    }
    params.push(userSpanType);
    return { clause: `r."spanType" = $${startIdx}`, nextIdx: startIdx + 1 };
  }
  const placeholders: string[] = [];
  for (const t of BRANCH_SPAN_TYPES) {
    placeholders.push(`$${startIdx + placeholders.length}`);
    params.push(t);
  }
  return { clause: `r."spanType" IN (${placeholders.join(', ')})`, nextIdx: startIdx + placeholders.length };
}

/**
 * Apply branch filters (spanType + the shared context surface). Branches do
 * NOT have a `parentSpanId IS NULL` predicate — branches can be nested under
 * other spans. Returns the bound params and the next param index.
 */
function buildListBranchesFilters(
  filters: ListBranchesArgs['filters'],
  spanType: string | undefined,
  spanTable: string,
  nextParamIdx: number,
): { conditions: string[]; params: unknown[]; nextParamIdx: number } | null {
  const conditions: string[] = [latestSpanPredicate(spanTable)];
  const params: unknown[] = [];
  let i = nextParamIdx;

  const spanTypeClause = buildBranchSpanTypeClause(spanType, params, i);
  if (!spanTypeClause) return null;
  conditions.push(spanTypeClause.clause);
  i = spanTypeClause.nextIdx;

  if (!filters) return { conditions, params, nextParamIdx: i };

  if (filters.startedAt?.start) {
    conditions.push(`r."startedAt" ${filters.startedAt.startExclusive ? '>' : '>='} $${i++}`);
    params.push(asIsoTimestamp(filters.startedAt.start));
  }
  if (filters.startedAt?.end) {
    conditions.push(`r."startedAt" ${filters.startedAt.endExclusive ? '<' : '<='} $${i++}`);
    params.push(asIsoTimestamp(filters.startedAt.end));
  }
  // A pending row stores a placeholder `endedAt`, so an endedAt range filter
  // has to exclude it: a span that has not ended has no end time to match.
  if (filters.endedAt?.start) {
    conditions.push(`(NOT r."isPending" AND r."endedAt" ${filters.endedAt.startExclusive ? '>' : '>='} $${i++})`);
    params.push(asIsoTimestamp(filters.endedAt.start));
  }
  if (filters.endedAt?.end) {
    conditions.push(`(NOT r."isPending" AND r."endedAt" ${filters.endedAt.endExclusive ? '<' : '<='} $${i++})`);
    params.push(asIsoTimestamp(filters.endedAt.end));
  }
  if (filters.traceId !== undefined) {
    conditions.push(`r."traceId" = $${i++}`);
    params.push(filters.traceId);
  }
  if (filters.entityType !== undefined) {
    conditions.push(`r."entityType" = $${i++}`);
    params.push(filters.entityType);
  }
  if (filters.entityId !== undefined) {
    conditions.push(`r."entityId" = $${i++}`);
    params.push(filters.entityId);
  }
  if (filters.entityName !== undefined) {
    conditions.push(`r."entityName" = $${i++}`);
    params.push(filters.entityName);
  }
  if (filters.entityVersionId !== undefined) {
    conditions.push(`r."entityVersionId" = $${i++}`);
    params.push(filters.entityVersionId);
  }
  if (filters.parentEntityType !== undefined) {
    conditions.push(`r."parentEntityType" = $${i++}`);
    params.push(filters.parentEntityType);
  }
  if (filters.parentEntityId !== undefined) {
    conditions.push(`r."parentEntityId" = $${i++}`);
    params.push(filters.parentEntityId);
  }
  if (filters.parentEntityName !== undefined) {
    conditions.push(`r."parentEntityName" = $${i++}`);
    params.push(filters.parentEntityName);
  }
  if (filters.parentEntityVersionId !== undefined) {
    conditions.push(`r."parentEntityVersionId" = $${i++}`);
    params.push(filters.parentEntityVersionId);
  }
  if (filters.rootEntityType !== undefined) {
    conditions.push(`r."rootEntityType" = $${i++}`);
    params.push(filters.rootEntityType);
  }
  if (filters.rootEntityId !== undefined) {
    conditions.push(`r."rootEntityId" = $${i++}`);
    params.push(filters.rootEntityId);
  }
  if (filters.rootEntityName !== undefined) {
    conditions.push(`r."rootEntityName" = $${i++}`);
    params.push(filters.rootEntityName);
  }
  if (filters.rootEntityVersionId !== undefined) {
    conditions.push(`r."rootEntityVersionId" = $${i++}`);
    params.push(filters.rootEntityVersionId);
  }
  if (filters.userId !== undefined) {
    conditions.push(`r."userId" = $${i++}`);
    params.push(filters.userId);
  }
  if (filters.organizationId !== undefined) {
    conditions.push(`r."organizationId" = $${i++}`);
    params.push(filters.organizationId);
  }
  if (filters.resourceId !== undefined) {
    conditions.push(`r."resourceId" = $${i++}`);
    params.push(filters.resourceId);
  }
  if (filters.runId !== undefined) {
    conditions.push(`r."runId" = $${i++}`);
    params.push(filters.runId);
  }
  if (filters.sessionId !== undefined) {
    conditions.push(`r."sessionId" = $${i++}`);
    params.push(filters.sessionId);
  }
  if (filters.threadId !== undefined) {
    conditions.push(`r."threadId" = $${i++}`);
    params.push(filters.threadId);
  }
  if (filters.requestId !== undefined) {
    conditions.push(`r."requestId" = $${i++}`);
    params.push(filters.requestId);
  }
  if (filters.experimentId !== undefined) {
    conditions.push(`r."experimentId" = $${i++}`);
    params.push(filters.experimentId);
  }
  if (filters.environment !== undefined) {
    conditions.push(`r."environment" = $${i++}`);
    params.push(filters.environment);
  }
  if (filters.serviceName !== undefined) {
    conditions.push(`r."serviceName" = $${i++}`);
    params.push(filters.serviceName);
  }
  if (filters.source !== undefined) {
    conditions.push(`r."executionSource" = $${i++}`);
    params.push(filters.source);
  }
  if (filters.metadata != null) {
    conditions.push(`r."metadataSearch" @> $${i++}::jsonb`);
    params.push(JSON.stringify(filters.metadata));
  }
  if (filters.tags != null && filters.tags.length > 0) {
    conditions.push(`r."tags" @> $${i++}::text[]`);
    params.push(filters.tags);
  }
  if (filters.status !== undefined) {
    switch (filters.status) {
      case TraceStatus.ERROR:
        conditions.push(`r."error" IS NOT NULL`);
        break;
      case TraceStatus.RUNNING:
        conditions.push(`r."isPending"`);
        break;
      case TraceStatus.SUCCESS:
        conditions.push(`r."error" IS NULL AND NOT r."isPending"`);
        break;
    }
  }

  return { conditions, params, nextParamIdx: i };
}

export async function listBranches(
  client: DbClient,
  schema: string,
  args: ListBranchesArgs,
): Promise<ListBranchesResponse> {
  const { mode, filters, pagination, orderBy, after, limit } = listBranchesArgsSchema.parse(args);
  const span = qualifiedTable(schema, TABLE_SPAN_EVENTS);

  if (mode === 'delta') {
    assertDeltaPollingEnabled();
    return listBranchesDelta(client, span, filters, after, limit);
  }

  return listBranchesPage(client, span, filters, pagination.page, pagination.perPage, orderBy.field, orderBy.direction);
}

async function listBranchesPage(
  client: DbClient,
  span: string,
  filters: ListBranchesArgs['filters'],
  page: number,
  perPage: number,
  orderField: 'startedAt' | 'endedAt',
  orderDir: 'ASC' | 'DESC',
): Promise<ListBranchesResponse> {
  const built = buildListBranchesFilters(filters, filters?.spanType, span, 1);
  if (!built) {
    // Caller asked for a non-branch spanType — nothing matches by definition.
    const deltaCursor = deltaPollingFeatureEnabled()
      ? await readBranchesStreamHeadCursor(client, span, filters)
      : undefined;
    return {
      pagination: { total: 0, page, perPage, hasMore: false },
      branches: [],
      ...(deltaCursor !== undefined ? { deltaCursor } : {}),
    };
  }

  const { conditions, params, nextParamIdx } = built;
  let i = nextParamIdx;
  const whereClause = `WHERE ${conditions.join(' AND ')}`;
  const orderClause =
    orderField === 'endedAt'
      ? `ORDER BY r."endedAt" ${orderDir} NULLS ${orderDir === 'DESC' ? 'FIRST' : 'LAST'}, r."cursorId" ${orderDir}`
      : `ORDER BY r."${orderField}" ${orderDir}, r."cursorId" ${orderDir}`;

  const countRow = await client.oneOrNone<{ count: string }>(
    `SELECT COUNT(*)::text AS count FROM ${span} r ${whereClause}`,
    params,
  );
  const count = Number(countRow?.count ?? 0);

  let spans: SpanRecord[] = [];
  if (count > 0) {
    const rows = await client.manyOrNone<Record<string, any>>(
      `SELECT ${SPAN_SELECT_COLUMNS_ALIASED}
       FROM ${span} r
       ${whereClause}
       ${orderClause}
       LIMIT $${i++} OFFSET $${i++}`,
      [...params, perPage, page * perPage],
    );
    spans = rows.map(rowToSpanRecord);
  }

  const deltaCursor = deltaPollingFeatureEnabled()
    ? await readBranchesStreamHeadCursor(client, span, filters)
    : undefined;

  return {
    pagination: { total: count, page, perPage, hasMore: (page + 1) * perPage < count },
    branches: toTraceSpans(spans),
    ...(deltaCursor !== undefined ? { deltaCursor } : {}),
  };
}

async function listBranchesDelta(
  client: DbClient,
  span: string,
  filters: ListBranchesArgs['filters'],
  after: string | undefined,
  limit: number,
): Promise<ListBranchesResponse> {
  if (after === undefined) {
    const deltaCursor = await readBranchesStreamHeadCursor(client, span, filters);
    return { branches: [], delta: { limit, hasMore: false }, deltaCursor };
  }

  const built = buildListBranchesFilters(filters, filters?.spanType, span, 1);
  if (!built) {
    return {
      branches: [],
      delta: { limit, hasMore: false },
      deltaCursor: await readBranchesStreamHeadCursor(client, span, filters),
    };
  }

  const { conditions, params, nextParamIdx } = built;
  let i = nextParamIdx;
  const afterCursor = decodeDeltaCursor(after);
  const safeHorizon = await readSafeXactHorizon(client);
  conditions.push(`(r."xactId", r."cursorId") > ($${i++}::xid8, $${i++}::bigint)`);
  params.push(afterCursor.xactId, afterCursor.cursorId);
  conditions.push(`r."xactId" < $${i++}::xid8`);
  params.push(safeHorizon);

  const rows = await client.manyOrNone<Record<string, any>>(
    `SELECT ${SPAN_SELECT_COLUMNS_ALIASED}
     FROM ${span} r
     WHERE ${conditions.join(' AND ')}
     ORDER BY r."xactId" ASC, r."cursorId" ASC
     LIMIT $${i++}`,
    [...params, limit + 1],
  );

  const hasMore = rows.length > limit;
  const visible = rows.slice(0, limit);
  const deltaCursor =
    visible.length > 0
      ? encodeDeltaCursor(visible[visible.length - 1]!.xactId, visible[visible.length - 1]!.cursorId)
      : encodeDeltaCursor(safeHorizon, 0);

  return {
    branches: toTraceSpans(visible.map(rowToSpanRecord)),
    delta: { limit, hasMore },
    deltaCursor,
  };
}

/**
 * Branch bootstrap cursor. It points at the safe transaction horizon, not at
 * the current max cursorId, so a late-committing branch cannot be skipped.
 */
async function readBranchesStreamHeadCursor(
  client: DbClient,
  span: string,
  filters: ListBranchesArgs['filters'],
): Promise<string> {
  void span;
  void filters;
  return encodeDeltaCursor(await readSafeXactHorizon(client), 0);
}
